<template>
  <MarketInsightCard
    :title="$t('market_insights.cumulative_re.title')"
    :description="$t('market_insights.cumulative_re.description')"
    icon="fa-solid fa-chart-line"
    :infoText="$t('market_insights.cumulative_re.info_text')"
  >
    <template #content>
      <!-- Country Dropdown -->
      <div class="mb-3">
        <label class="form-label form-label-sm">{{
          $t("market_insights.cumulative_re.country_label")
        }}</label>
        <select
          class="form-select form-select-sm"
          v-model="selectedCountry"
          :disabled="isLoading"
        >
          <option
            v-for="country in countryOptions"
            :key="country.value"
            :value="country.value"
          >
            {{ country.label }}
          </option>
        </select>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="chart-loading-placeholder">
        <font-awesome-icon icon="fa-solid fa-spinner" spin class="me-2" />
        {{ $t("market_insights.cumulative_re.loading") }}
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger mb-0">
        <font-awesome-icon
          icon="fa-solid fa-exclamation-triangle"
          class="me-2"
        />
        {{ $t("market_insights.cumulative_re.error") }}
      </div>

      <!-- Chart -->
      <LineChart
        v-else-if="chartData && chartData.datasets.length"
        :chartData="chartData"
        :yAxisTitle="$t('market_insights.cumulative_re.y_axis_label')"
        :height="300"
        :showLegend="true"
        legendPosition="bottom"
      />
    </template>
  </MarketInsightCard>
</template>

<script setup lang="ts">
import LineChart from "@/common/LineChart.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { EU_GENERATION_DATA_URL, MONTH_NAMES } from "@/constants";
import { LineDataset } from "@/types/ChartData";
import { groupBy, sortBy } from "lodash-es";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import MarketInsightCard from "./MarketInsightCard.vue";

interface EnergyData {
  year: number;
  month: number;
  country: string;
  cum_renewable_energy: number;
  renewable_energy_generated: number;
  non_renewable_energy_generated: number;
}

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

// Sequential forest palette: most recent year gets the darkest (focal) stop
import { PALETTE } from "@/composables/chartPalette";

const YEAR_COLORS = [...PALETTE.sequentialForest].reverse();

// State
const selectedCountry = ref("EU");
const rawData = ref<EnergyData[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

// Fetch data
const fetchData = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const response = await fetch(EU_GENERATION_DATA_URL);
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    rawData.value = await response.json();
  } catch (err) {
    error.value = t("market_insights.cumulative_re.error");
    globalErrorHandler(err);
  } finally {
    isLoading.value = false;
  }
};

// Available countries from dataset
const availableCountries = computed(() => {
  const countries = [...new Set(rawData.value.map((r) => r.country))];
  const euFirst = countries.filter((c) => c === "EU");
  const others = countries.filter((c) => c !== "EU").sort();
  return [...euFirst, ...others];
});

// Country options for dropdown with proper labels
const countryOptions = computed(() => {
  return availableCountries.value.map((code) => ({
    value: code,
    label:
      code === "EU" ? t("market_insights.cumulative_re.eu_aggregate") : code,
  }));
});

// Filter data by selected country
const countryData = computed(() => {
  return rawData.value.filter(
    (record) => record.country === selectedCountry.value
  );
});

// Group by year
const dataByYear = computed(() => {
  return groupBy(countryData.value, "year");
});

// Transform to chart data
const chartData = computed(() => {
  const years = Object.keys(dataByYear.value).sort();
  const monthLabels = MONTH_NAMES;

  const datasets: LineDataset[] = years.map((year, index) => {
    const yearRecords = sortBy(dataByYear.value[year], "month");
    const data = monthLabels.map((_, monthIndex) => {
      const record = yearRecords.find((r) => r.month === monthIndex + 1);
      return record ? Math.round(record.cum_renewable_energy / 1000) : null;
    });

    const color = YEAR_COLORS[index % YEAR_COLORS.length];
    const isLatestYear = index === years.length - 1;

    // Find the last non-null data point index for the latest year
    const lastDataIndex: number = data.reduce<number>(
      (last, val, i) => (val !== null ? i : last),
      -1
    );

    return {
      label: year,
      data: data,
      borderColor: color,
      backgroundColor: color,
      tension: 0.4,
      fill: false,
      spanGaps: false,
      pointRadius: 3,
      pointHoverRadius: 5,
      borderWidth: 2,
      // Dash the final segment of the latest year (ongoing month, not final)
      ...(isLatestYear && lastDataIndex > 0
        ? {
            segment: {
              borderDash: (ctx: { p0DataIndex: number }) =>
                ctx.p0DataIndex >= lastDataIndex - 1 ? [5, 4] : undefined,
            },
          }
        : {}),
    };
  });

  return {
    labels: monthLabels,
    datasets: datasets,
  };
});

// Fetch data on mount
onMounted(() => {
  fetchData();
});
</script>

<style scoped lang="scss">
.form-label-sm {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.chart-loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  border-radius: 0.5rem;
  background-color: var(--phoenix-gray-1000);
  color: var(--phoenix-gray-400);
  font-size: 0.875rem;
}
</style>
